// ---------------------------------------------------------------------------
// SharedSignalsPage — View and manage signals you've shared with other users.
//
// Shows a table of shared signals with expiry countdown, and actions to
// view, delete, or export signals.
// ---------------------------------------------------------------------------

import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  mergeClasses,
  Select,
  Spinner,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  ArrowDownload24Regular,
  Copy24Regular,
  Delete24Regular,
  Dismiss24Regular,
  Eye24Regular,
  Filter24Regular,
  Grid24Regular,
  Person24Regular,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { useAuth } from '../../hooks/useAuth/useAuth.js';
import {
  getGraphProfilePhoto,
  getGraphUserDisplayName,
} from '../../services/profilePhotosService.js';
import {
  checkSharedSignalsAccess,
  getSharedSignalPayload,
  listAllSharedSignals,
  listSharedSignals,
  unshareSignal,
} from '../../services/sharedSignalsService.js';
import { useScenarioFactory } from '../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import { ScenarioName } from '../../telemetry/scenarios.js';
import {
  ActionOutcome,
  ActionScenario,
  ActionScenarioType,
  EntityType,
  getOperationActionScenario,
  OperationType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';
import type { SharedSignalResponse } from '../../types/sharedSignal.js';

import { useStyles } from './SharedSignalsPage.style.js';

const OWNER_PROFILE_CONCURRENCY = 10;

function formatTimeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
}

function getExpiryClass(
  expiresAt: string,
  styles: ReturnType<typeof useStyles>,
): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  const days = diff / (1000 * 60 * 60 * 24);
  if (days > 14) return styles.expiryGreen;
  if (days > 3) return styles.expiryYellow;
  return styles.expiryRed;
}

function formatResponseQuality(value: SharedSignalResponse['responseQuality']) {
  if (value === 'good') return 'Good';
  if (value === 'neutral') return 'Neutral';
  if (value === 'bad') return 'Bad';
  return 'Not specified';
}

function formatEvalAssessment(value: SharedSignalResponse['evalAssessment']) {
  if (value === 'agree') return 'Agree';
  if (value === 'neutral') return 'Neutral';
  if (value === 'disagree') return 'Disagree';
  return 'Not specified';
}

export function SharedSignalsPage() {
  const styles = useStyles();
  const httpClient = useHttpClient();
  const { getGraphToken } = useAuth();
  const navigate = useNavigate();
  const scenarioFactory = useScenarioFactory();
  const { recordAction } = useUserBi();

  const [signals, setSignals] = useState<SharedSignalResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'my'>('my');
  const [hasViewAllAccess, setHasViewAllAccess] = useState<boolean | null>(
    null,
  );
  const [currentUserOid, setCurrentUserOid] = useState<string | null>(null);
  const [profilePhotos, setProfilePhotos] = useState<Record<string, string>>(
    {},
  );
  const [ownerDisplayNames, setOwnerDisplayNames] = useState<
    Record<string, string>
  >({});
  const attemptedProfilePhotos = useRef(new Set<string>());
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Check eligibility on mount
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let accessResult: { hasAccess: boolean; userOid: string } | null = null;
      try {
        accessResult = await checkSharedSignalsAccess(httpClient);
        if (cancelled) return;
        setHasViewAllAccess(accessResult.hasAccess);
        setCurrentUserOid(accessResult.userOid);
      } catch {
        if (cancelled) return;
        setHasViewAllAccess(false);
      }

      // Now load signals with the correct filter based on access result
      const useAll = accessResult?.hasAccess === true;
      if (useAll) {
        setFilter('all');
      }

      try {
        const data = useAll
          ? await listAllSharedSignals(httpClient)
          : await listSharedSignals(httpClient);
        if (cancelled) return;
        setSignals(data);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load signals');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [httpClient]);

  useEffect(() => {
    const ownerIds = [
      ...new Set(
        signals
          .map((signal) => signal.ownerId)
          .filter((ownerId): ownerId is string => Boolean(ownerId)),
      ),
    ].filter((ownerId) => !attemptedProfilePhotos.current.has(ownerId));

    ownerIds.forEach((ownerId) => attemptedProfilePhotos.current.add(ownerId));

    void (async () => {
      const profiles = [];

      for (
        let start = 0;
        start < ownerIds.length;
        start += OWNER_PROFILE_CONCURRENCY
      ) {
        const batch = ownerIds.slice(start, start + OWNER_PROFILE_CONCURRENCY);
        profiles.push(
          ...(await Promise.all(
            batch.map(async (ownerId) => {
              const [photoResult, nameResult] = await Promise.allSettled([
                getGraphProfilePhoto(getGraphToken, ownerId),
                getGraphUserDisplayName(getGraphToken, ownerId),
              ]);

              if (photoResult.status === 'rejected') {
                console.warn(
                  `Failed to load the profile photo for user ${ownerId}.`,
                  photoResult.reason,
                );
              }
              if (nameResult.status === 'rejected') {
                console.warn(
                  `Failed to load the display name for user ${ownerId}.`,
                  nameResult.reason,
                );
              }

              return {
                ownerId,
                photoUrl:
                  photoResult.status === 'fulfilled'
                    ? photoResult.value
                    : undefined,
                displayName:
                  nameResult.status === 'fulfilled'
                    ? nameResult.value
                    : undefined,
              };
            }),
          )),
        );
      }

      return profiles;
    })().then((profiles) => {
      if (!isMounted.current) return;
      setProfilePhotos((current) => {
        const next = { ...current };
        for (const profile of profiles) {
          if (profile.photoUrl) next[profile.ownerId] = profile.photoUrl;
        }
        return next;
      });
      setOwnerDisplayNames((current) => {
        const next = { ...current };
        for (const profile of profiles) {
          if (profile.displayName) {
            next[profile.ownerId] = profile.displayName;
          }
        }
        return next;
      });
    });
  }, [getGraphToken, signals]);

  const loadSignals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data =
        filter === 'all' && hasViewAllAccess
          ? await listAllSharedSignals(httpClient)
          : await listSharedSignals(httpClient);
      setSignals(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load signals');
    } finally {
      setLoading(false);
    }
  }, [httpClient, filter, hasViewAllAccess]);

  // Reload when filter changes (but not on initial mount — that's handled above)
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  useEffect(() => {
    if (!initialLoadDone) {
      if (hasViewAllAccess !== null) setInitialLoadDone(true);
      return;
    }
    void loadSignals();
  }, [filter, initialLoadDone, hasViewAllAccess, loadSignals]);

  const handleDelete = useCallback(
    async (signalId: string) => {
      setDeletingId(signalId);
      try {
        recordAction({
          panelType: PanelType.SharedSignalsPage,
          panelRegion: PanelRegion.Main,
          actionScenario: getOperationActionScenario(
            EntityType.SharedSignal,
            OperationType.Delete,
          ),
          actionScenarioType: OperationType.Delete,
          actionGesture: 'click',
          actionOutcome: ActionOutcome.Requested,
        });
        await unshareSignal(httpClient, signalId);
        setSignals((prev) => prev.filter((s) => s.signalId !== signalId));
      } catch (err) {
        console.error('Failed to delete signal:', err);
      } finally {
        setDeletingId(null);
      }
    },
    [httpClient, recordAction],
  );

  const handleView = useCallback(
    (signal: SharedSignalResponse) => {
      recordAction({
        panelType: PanelType.SharedSignalsPage,
        panelRegion: PanelRegion.Main,
        actionScenario: ActionScenario.OpenSharedSignal,
        actionScenarioType: ActionScenarioType.Open,
      });
      const itemId = signal.itemId ?? signal.signalId;
      void navigate(`/debug?cv=${encodeURIComponent(itemId)}`);
    },
    [navigate, recordAction],
  );

  const handleCopyLink = useCallback(
    (signal: SharedSignalResponse) => {
      recordAction({
        panelType: PanelType.SharedSignalsPage,
        panelRegion: PanelRegion.Main,
        actionScenario: ActionScenario.CopySharedSignalLink,
        actionScenarioType: ActionScenarioType.Open,
      });
      const itemId = signal.itemId ?? signal.signalId;
      const shareUrl = `${window.location.origin}/debug?cv=${encodeURIComponent(itemId)}`;
      void navigator.clipboard.writeText(shareUrl);
    },
    [recordAction],
  );

  const [exporting, setExporting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmExport, setConfirmExport] = useState(false);

  const handleExportAll = useCallback(async () => {
    setExporting(true);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.SharedSignalsExport,
    );
    try {
      // Only export the current user's own signals regardless of filter view
      const mySignals = currentUserOid
        ? signals.filter((s) => s.ownerId === currentUserOid)
        : signals;
      const payloads: Record<string, unknown>[] = [];
      let succeededCount = 0;
      let failedCount = 0;
      for (const signal of mySignals) {
        try {
          const payload = await getSharedSignalPayload(
            httpClient,
            signal.signalId,
          );
          payloads.push(JSON.parse(payload) as Record<string, unknown>);
          succeededCount += 1;
        } catch {
          failedCount += 1;
          payloads.push({
            signalId: signal.signalId,
            error: 'No payload associated with signalId',
          });
        }
      }
      const jsonStr = JSON.stringify(payloads, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'shared-signals.json';
      a.click();
      URL.revokeObjectURL(url);
      scenario.complete('success', {
        requested_count: mySignals.length,
        succeeded_count: succeededCount,
        failed_count: failedCount,
      });
    } catch (error) {
      scenario.complete('failure');
      throw error;
    } finally {
      setExporting(false);
    }
  }, [signals, httpClient, currentUserOid, scenarioFactory]);

  // ---- Sorting ----
  type SortKey =
    | 'itemId'
    | 'ownerId'
    | 'agentName'
    | 'responseQuality'
    | 'evalAssessment'
    | 'promptPreview'
    | 'description'
    | 'sharedAt'
    | 'expiresAt';
  const [sortBy, setSortBy] = useState<SortKey>('sharedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortBy === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(key);
        setSortDir('desc');
      }
    },
    [sortBy],
  );

  // ---- Column filters ----
  const [showFilters, setShowFilters] = useState(false);
  const emptyFilters = {
    agentName: '',
    responseQuality: '',
    evalAssessment: '',
    promptPreview: '',
    description: '',
    sharedAt: '',
    expiresAt: '',
    ownerId: '',
    itemId: '',
  };
  const [columnFilters, setColumnFilters] = useState(emptyFilters);
  const hasActiveFilters = Object.values(columnFilters).some((v) => v !== '');

  const updateFilter = (key: keyof typeof columnFilters, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setColumnFilters(emptyFilters);
    setPage(0);
  };

  // ---- Filtering ----
  // When "All" is selected and user has access, data comes from /all endpoint.
  // When "My" is selected, data comes from the standard list endpoint (already filtered server-side).
  const filteredSignals = useMemo(() => {
    return signals.filter((s) => {
      const lc = (v: string | undefined | null) => (v ?? '').toLowerCase();
      for (const [key, filterValue] of Object.entries(columnFilters)) {
        if (!filterValue) continue;
        const needle = filterValue.toLowerCase();
        const field = key as keyof typeof columnFilters;
        if (field === 'sharedAt') {
          if (
            !new Date(s.sharedAt)
              .toLocaleDateString()
              .toLowerCase()
              .includes(needle)
          )
            return false;
        } else if (field === 'expiresAt') {
          if (!formatTimeRemaining(s.expiresAt).toLowerCase().includes(needle))
            return false;
        } else if (field === 'ownerId') {
          const ownerLabel = ownerDisplayNames[s.ownerId] ?? s.ownerId;
          if (!lc(ownerLabel).includes(needle)) return false;
        } else if (field === 'responseQuality') {
          if ((s.responseQuality ?? 'unspecified') !== filterValue)
            return false;
        } else if (field === 'evalAssessment') {
          if ((s.evalAssessment ?? 'unspecified') !== filterValue) return false;
        } else {
          if (!lc(s[field]).includes(needle)) return false;
        }
      }
      return true;
    });
  }, [signals, columnFilters, ownerDisplayNames]);

  const sortedSignals = useMemo(() => {
    const sorted = [...filteredSignals];
    sorted.sort((a, b) => {
      const aVal =
        sortBy === 'ownerId'
          ? (ownerDisplayNames[a.ownerId] ?? a.ownerId)
          : (a[sortBy] ?? '');
      const bVal =
        sortBy === 'ownerId'
          ? (ownerDisplayNames[b.ownerId] ?? b.ownerId)
          : (b[sortBy] ?? '');
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredSignals, ownerDisplayNames, sortBy, sortDir]);

  // ---- Column flex proportions ----
  const colFlex = {
    agentName: 1.2,
    responseQuality: 0.8,
    evalAssessment: 0.9,
    promptPreview: 2,
    description: 2,
    sharedAt: 0.8,
    expiresAt: 0.8,
    ownerId: 1.2,
    itemId: 1.5,
  };

  const sortIndicator = (key: SortKey) =>
    sortBy === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '';

  // ---- Pagination ----
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(sortedSignals.length / PAGE_SIZE);
  const pagedSignals = sortedSignals.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE,
  );

  if (loading) {
    return (
      <div className={styles.emptyState}>
        <Spinner label="Loading shared signals…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.emptyState}>
        <Text>{error}</Text>
        <Button onClick={() => void loadSignals()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {hasViewAllAccess === false && (
        <MessageBar intent="info" style={{ marginBottom: '12px' }}>
          <MessageBarBody>
            To view signals shared by all users, request the{' '}
            <strong>MstAIStudio-View-Shared-Signals</strong> eligibility.{' '}
            <Link
              href="https://m365pulse.microsoft.com/CorpIdentity/RequestAccess/RequestScenario?scenarioName=AIStudio-View-Shared-Signals"
              target="_blank"
              rel="noopener noreferrer"
              inline
            >
              Request access here
            </Link>
          </MessageBarBody>
        </MessageBar>
      )}
      <div className={styles.header}>
        <Text size={600} weight="semibold">
          Shared Signals
        </Text>
        <div className={styles.headerControls}>
          <div className={styles.filterToggle}>
            <Tooltip
              content={
                hasViewAllAccess === false
                  ? 'Requires MstAIStudio-View-Shared-Signals eligibility'
                  : 'Show signals shared by all users'
              }
              relationship="description"
            >
              <Button
                appearance={filter === 'all' ? 'primary' : 'outline'}
                icon={<Grid24Regular />}
                size="medium"
                disabled={hasViewAllAccess === false}
                onClick={() => {
                  setFilter('all');
                  setPage(0);
                }}
              >
                All
              </Button>
            </Tooltip>
            <Button
              appearance={filter === 'my' ? 'primary' : 'outline'}
              icon={<Person24Regular />}
              size="medium"
              onClick={() => {
                setFilter('my');
                setPage(0);
              }}
            >
              My
            </Button>
          </div>
          <Button
            appearance="outline"
            icon={
              exporting ? <Spinner size="tiny" /> : <ArrowDownload24Regular />
            }
            disabled={signals.length === 0 || exporting}
            onClick={() => setConfirmExport(true)}
          >
            {exporting ? 'Exporting…' : 'Export My Signals'}
          </Button>
          <Tooltip
            content={showFilters ? 'Hide filters' : 'Show filters'}
            relationship="description"
          >
            <Button
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
              appearance={hasActiveFilters ? 'primary' : 'outline'}
              icon={<Filter24Regular />}
              onClick={() => setShowFilters((v) => !v)}
            />
          </Tooltip>
        </div>
      </div>

      {signals.length === 0 ? (
        <div className={styles.emptyState}>
          <Text>
            {filter === 'my'
              ? "You haven't shared any signals yet."
              : 'No shared signals found.'}
          </Text>
          <Text size={200}>
            Use the Share button on a signal detail view to share it with other
            users.
          </Text>
        </div>
      ) : (
        <>
          <div className={styles.entries}>
            <div className={styles.rowHeader}>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.agentName, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'agentName' && styles.headerSortActive,
                )}
                onClick={() => handleSort('agentName')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('agentName');
                  }
                }}
              >
                Agent{sortIndicator('agentName')}
              </span>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.responseQuality, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'responseQuality' && styles.headerSortActive,
                )}
                onClick={() => handleSort('responseQuality')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('responseQuality');
                  }
                }}
              >
                Response{sortIndicator('responseQuality')}
              </span>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.evalAssessment, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'evalAssessment' && styles.headerSortActive,
                )}
                onClick={() => handleSort('evalAssessment')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('evalAssessment');
                  }
                }}
              >
                Evals{sortIndicator('evalAssessment')}
              </span>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.promptPreview, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'promptPreview' && styles.headerSortActive,
                )}
                onClick={() => handleSort('promptPreview')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('promptPreview');
                  }
                }}
              >
                Prompt{sortIndicator('promptPreview')}
              </span>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.description, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'description' && styles.headerSortActive,
                )}
                onClick={() => handleSort('description')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('description');
                  }
                }}
              >
                Description{sortIndicator('description')}
              </span>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.sharedAt, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'sharedAt' && styles.headerSortActive,
                )}
                onClick={() => handleSort('sharedAt')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('sharedAt');
                  }
                }}
              >
                Shared Date{sortIndicator('sharedAt')}
              </span>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.expiresAt, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'expiresAt' && styles.headerSortActive,
                )}
                onClick={() => handleSort('expiresAt')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('expiresAt');
                  }
                }}
              >
                Time Left{sortIndicator('expiresAt')}
              </span>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.ownerId, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'ownerId' && styles.headerSortActive,
                )}
                onClick={() => handleSort('ownerId')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('ownerId');
                  }
                }}
              >
                Shared By{sortIndicator('ownerId')}
              </span>
              <span
                role="button"
                tabIndex={0}
                style={{ flex: colFlex.itemId, minWidth: 0 }}
                className={mergeClasses(
                  styles.headerCell,
                  styles.headerSortable,
                  sortBy === 'itemId' && styles.headerSortActive,
                )}
                onClick={() => handleSort('itemId')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSort('itemId');
                  }
                }}
              >
                CV{sortIndicator('itemId')}
              </span>
              <span className={styles.colActions}>Actions</span>
            </div>
            {showFilters && (
              <div className={styles.filterBar}>
                <Input
                  size="small"
                  placeholder="Agent"
                  value={columnFilters.agentName}
                  onChange={(_e, d) => updateFilter('agentName', d.value)}
                  style={{ flex: colFlex.agentName, minWidth: 0 }}
                />
                <Select
                  size="small"
                  aria-label="Filter by response quality"
                  value={columnFilters.responseQuality}
                  onChange={(_e, d) => updateFilter('responseQuality', d.value)}
                  style={{ flex: colFlex.responseQuality, minWidth: 0 }}
                >
                  <option value="">All responses</option>
                  <option value="good">Good</option>
                  <option value="neutral">Neutral</option>
                  <option value="bad">Bad</option>
                  <option value="unspecified">Not specified</option>
                </Select>
                <Select
                  size="small"
                  aria-label="Filter by eval assessment"
                  value={columnFilters.evalAssessment}
                  onChange={(_e, d) => updateFilter('evalAssessment', d.value)}
                  style={{ flex: colFlex.evalAssessment, minWidth: 0 }}
                >
                  <option value="">All evals</option>
                  <option value="agree">Agree</option>
                  <option value="neutral">Neutral</option>
                  <option value="disagree">Disagree</option>
                  <option value="unspecified">Not specified</option>
                </Select>
                <Input
                  size="small"
                  placeholder="Prompt"
                  value={columnFilters.promptPreview}
                  onChange={(_e, d) => updateFilter('promptPreview', d.value)}
                  style={{ flex: colFlex.promptPreview, minWidth: 0 }}
                />
                <Input
                  size="small"
                  placeholder="Description"
                  value={columnFilters.description}
                  onChange={(_e, d) => updateFilter('description', d.value)}
                  style={{ flex: colFlex.description, minWidth: 0 }}
                />
                <Input
                  size="small"
                  placeholder="Date"
                  value={columnFilters.sharedAt}
                  onChange={(_e, d) => updateFilter('sharedAt', d.value)}
                  style={{ flex: colFlex.sharedAt, minWidth: 0 }}
                />
                <Input
                  size="small"
                  placeholder="Time"
                  value={columnFilters.expiresAt}
                  onChange={(_e, d) => updateFilter('expiresAt', d.value)}
                  style={{ flex: colFlex.expiresAt, minWidth: 0 }}
                />
                <Input
                  size="small"
                  placeholder="Shared By"
                  value={columnFilters.ownerId}
                  onChange={(_e, d) => updateFilter('ownerId', d.value)}
                  style={{ flex: colFlex.ownerId, minWidth: 0 }}
                />
                <Input
                  size="small"
                  placeholder="CV"
                  value={columnFilters.itemId}
                  onChange={(_e, d) => updateFilter('itemId', d.value)}
                  style={{ flex: colFlex.itemId, minWidth: 0 }}
                />
                <span className={styles.colActions}>
                  <Tooltip content="Clear filters" relationship="label">
                    <Button
                      appearance="subtle"
                      icon={<Dismiss24Regular />}
                      size="small"
                      disabled={!hasActiveFilters}
                      onClick={clearFilters}
                    />
                  </Tooltip>
                </span>
              </div>
            )}
            {pagedSignals.map((signal) => (
              <div key={signal.signalId} className={styles.row}>
                <span
                  className={styles.cellText}
                  style={{ flex: colFlex.agentName, minWidth: 0 }}
                  title={signal.agentName ?? ''}
                >
                  {signal.agentName ?? '—'}
                </span>
                <span
                  className={styles.cellText}
                  style={{ flex: colFlex.responseQuality, minWidth: 0 }}
                >
                  {formatResponseQuality(signal.responseQuality)}
                </span>
                <span
                  className={styles.cellText}
                  style={{ flex: colFlex.evalAssessment, minWidth: 0 }}
                >
                  {formatEvalAssessment(signal.evalAssessment)}
                </span>
                <span
                  className={styles.cellText}
                  style={{ flex: colFlex.promptPreview, minWidth: 0 }}
                  title={signal.promptPreview ?? ''}
                >
                  {signal.promptPreview ?? '—'}
                </span>
                <span
                  className={styles.cellText}
                  style={{ flex: colFlex.description, minWidth: 0 }}
                  title={signal.description ?? ''}
                >
                  {signal.description ?? '—'}
                </span>
                <span
                  className={styles.cellText}
                  style={{ flex: colFlex.sharedAt, minWidth: 0 }}
                >
                  {new Date(signal.sharedAt).toLocaleDateString()}
                </span>
                <span
                  className={mergeClasses(
                    styles.cellText,
                    getExpiryClass(signal.expiresAt, styles),
                  )}
                  style={{ flex: colFlex.expiresAt, minWidth: 0 }}
                >
                  {formatTimeRemaining(signal.expiresAt)}
                </span>
                <div
                  className={styles.ownerCell}
                  style={{ flex: colFlex.ownerId, minWidth: 0 }}
                  title={signal.ownerId}
                >
                  <Avatar
                    name={ownerDisplayNames[signal.ownerId] ?? signal.ownerId}
                    size={24}
                    image={
                      profilePhotos[signal.ownerId]
                        ? { src: profilePhotos[signal.ownerId] }
                        : undefined
                    }
                  />
                  <span className={styles.ownerIdText}>
                    {ownerDisplayNames[signal.ownerId] ?? signal.ownerId}
                  </span>
                </div>
                <span
                  className={styles.cellText}
                  style={{ flex: colFlex.itemId, minWidth: 0 }}
                >
                  {signal.itemId ?? '—'}
                </span>
                <span className={styles.colActions}>
                  <Tooltip content="View signal" relationship="label">
                    <Button
                      as="a"
                      href={`/debug?cv=${encodeURIComponent(signal.itemId ?? signal.signalId)}`}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        handleView(signal);
                      }}
                      appearance="subtle"
                      icon={<Eye24Regular />}
                      size="small"
                    />
                  </Tooltip>
                  <Tooltip content="Copy share link" relationship="label">
                    <Button
                      appearance="subtle"
                      icon={<Copy24Regular />}
                      size="small"
                      onClick={() => handleCopyLink(signal)}
                    />
                  </Tooltip>
                  <Tooltip content="Delete signal" relationship="label">
                    <Button
                      appearance="subtle"
                      icon={
                        deletingId === signal.signalId ? (
                          <Spinner size="tiny" />
                        ) : (
                          <Delete24Regular />
                        )
                      }
                      size="small"
                      disabled={
                        deletingId === signal.signalId ||
                        (currentUserOid !== null &&
                          signal.ownerId !== currentUserOid)
                      }
                      onClick={() => setConfirmDeleteId(signal.signalId)}
                    />
                  </Tooltip>
                </span>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                appearance="subtle"
                size="small"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Previous
              </Button>
              <Text size={200}>
                Page {page + 1} of {totalPages}
              </Text>
              <Button
                appearance="subtle"
                size="small"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmDeleteId !== null}
        onOpenChange={(_, d) => {
          if (!d.open) setConfirmDeleteId(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Shared Signal</DialogTitle>
            <DialogContent>
              Are you sure you want to delete this shared signal? Other users
              will no longer be able to access it via the share link.
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => {
                  if (confirmDeleteId) {
                    void handleDelete(confirmDeleteId);
                  }
                  setConfirmDeleteId(null);
                }}
              >
                Delete
              </Button>
              <Button
                appearance="secondary"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Export confirmation dialog */}
      <Dialog
        open={confirmExport}
        onOpenChange={(_, d) => {
          if (!d.open) setConfirmExport(false);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Export My Signals</DialogTitle>
            <DialogContent>
              This will download all{' '}
              {currentUserOid
                ? signals.filter((s) => s.ownerId === currentUserOid).length
                : signals.length}{' '}
              of your shared signal payloads as a single JSON file. This may
              take a moment.
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => {
                  setConfirmExport(false);
                  void handleExportAll();
                }}
              >
                Export
              </Button>
              <Button
                appearance="secondary"
                onClick={() => setConfirmExport(false)}
              >
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
